import { logError } from "./customLogger";

interface DownloadFileOptions {
  file: File;
  fileName?: string;
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

export const downloadFile = async ({
  file,
  fileName,
  onSuccess,
  onError,
}: DownloadFileOptions): Promise<void> => {
  try {
    // Create blob URL from file
    const blobUrl = window.URL.createObjectURL(file);

    // Create temporary anchor element
    const link = document.createElement("a");
    link.href = blobUrl;
    link.style.display = "none";
    link.download = fileName || file.name;

    // Append to DOM, trigger click, and schedule cleanup
    document.body.appendChild(link);
    link.click();

    // Clean up after a short delay to ensure download starts
    setTimeout(() => {
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    }, 100);

    // Call success callback if provided
    if (onSuccess) {
      onSuccess();
    }
  } catch (error) {
    const err = error as Error;
    logError(err, "Error in downloadFile");
    if (onError) {
      onError(err);
    }
  }
};

export const downloadFileFromUrl = async (
  url: string,
  fileName: string,
): Promise<void> => {
  try {
    // Fetch the file as a blob to avoid CORS issues
    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/pdf",
      },
    });

    if (!response.ok) {
      console.error(`Failed to fetch file: ${response.statusText}`);
      return;
    }

    const blob = await response.blob();
    const file = new File([blob], fileName, { type: blob.type });

    await downloadFile({ file, fileName });
  } catch (error) {
    logError(error as Error, "Error in downloadFileFromUrl");
  }
};
